/**
 * IftIndianHeritage — "Indian culture and heritage across generations."
 *
 * Content preserved from the original indianfamilytree.com/indian-heritage
 * marketing page. Each list item deep-links into the equivalent
 * heritage-encyclopedia stub (rendered on demand by the Location AI
 * pipeline via /indian-heritage/<slug>).
 *
 * Route: /indian-heritage
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import SeoHead from "@/components/SeoHead";

const P = {
  bg: "#FDF9EF", ink: "#3A1F0F", inkSoft: "#6B4A2E",
  saffron: "#B25D38", saffronSoft: "#F7D6C1",
  green: "#0E7C55", gold: "#C08A2E", goldSoft: "#F5E6C4",
};

const SECTIONS = [
  {
    id: "family-values", kicker: "Family life", h: "The values a family carries",
    intro: "Traditional Indian families often emphasize a set of quiet, everyday principles — the invisible scaffolding that holds a household together across generations.",
    items: [
      ["Respect for elders", "respect-for-elders"],
      ["Strong intergenerational relationships", "intergenerational"],
      ["Shared responsibilities", "shared-responsibilities"],
      ["Hospitality toward guests", "hospitality"],
      ["Community service", "community-service"],
      ["Preservation of traditions", "preservation"],
      ["Lifelong family bonds", "lifelong-bonds"],
    ],
  },
  {
    id: "languages", kicker: "22 scheduled tongues · hundreds of dialects", h: "The languages of the subcontinent",
    intro: "India recognises 22 scheduled languages and hosts hundreds more. Every family carries at least one — many carry three or four — and the transitions between them tell a migration story.",
    items: [["Indo-Aryan", "indo-aryan"], ["Dravidian", "dravidian"], ["Tibeto-Burman", "tibeto-burman"], ["Austroasiatic", "austroasiatic"]],
  },
  {
    id: "village-heritage", kicker: "Where roots live", h: "Village heritage",
    intro: "For much of India's history, villages have been the heart of social and cultural life. Even today many families trace their ancestry to a specific village whose temples, ponds, fields and elders remember their name.",
    items: [
      ["Traditional occupations", "traditional-occupations"], ["Folk music", "folk-music"],
      ["Oral storytelling", "oral-storytelling"], ["Local festivals", "local-festivals"],
      ["Agricultural practices", "agricultural-practices"], ["Sacred groves", "sacred-groves"],
      ["Water management systems", "water-management"], ["Community celebrations", "community-celebrations"],
    ],
  },
  {
    id: "religions", kicker: "Birthplace of four · home to many", h: "A rich religious heritage",
    intro: "India is the birthplace of Hinduism, Buddhism, Jainism and Sikhism, and has long been home to thriving communities of Islam, Christianity, Judaism, Zoroastrianism, the Baháʼí Faith and indigenous tribal beliefs.",
    items: [
      ["Hinduism", "hinduism"], ["Buddhism", "buddhism"], ["Jainism", "jainism"], ["Sikhism", "sikhism"],
      ["Islam", "islam"], ["Christianity", "christianity"], ["Judaism", "judaism"],
      ["Zoroastrianism (Parsis)", "zoroastrianism"], ["Baháʼí Faith", "bahai"], ["Indigenous tribal beliefs", "tribal-beliefs"],
    ],
  },
  {
    id: "festivals", kicker: "The year on a family calendar", h: "Festivals — celebrating life together",
    intro: "India celebrates thousands of festivals through the year, many deeply tied to family and community. Every region — and often every household — keeps its own small rituals that make each celebration distinctive.",
    items: [
      ["Diwali", "diwali"], ["Holi", "holi"], ["Navratri", "navratri"], ["Dussehra", "dussehra"],
      ["Raksha Bandhan", "raksha-bandhan"], ["Janmashtami", "janmashtami"], ["Ganesh Chaturthi", "ganesh-chaturthi"],
      ["Pongal", "pongal"], ["Onam", "onam"], ["Baisakhi", "baisakhi"],
      ["Eid al-Fitr", "eid-al-fitr"], ["Eid al-Adha", "eid-al-adha"], ["Christmas", "christmas"],
      ["Guru Nanak Jayanti", "guru-nanak-jayanti"], ["Buddha Purnima", "buddha-purnima"], ["Mahavir Jayanti", "mahavir-jayanti"],
    ],
  },
  {
    id: "arts-crafts", kicker: "Hands that remember", h: "Traditional arts & crafts",
    intro: "Indian artisans have passed down remarkable skills through generations — many of these traditions remain family professions, carried by workshops that outlast dynasties.",
    items: [
      ["Madhubani painting", "madhubani"], ["Pattachitra", "pattachitra"], ["Kalamkari", "kalamkari"],
      ["Warli art", "warli-art"], ["Tanjore painting", "tanjore-painting"], ["Pashmina weaving", "pashmina"],
      ["Banarasi silk", "banarasi-silk"], ["Kanchipuram silk", "kanchipuram-silk"],
      ["Chikankari embroidery", "chikankari"], ["Blue pottery", "blue-pottery"], ["Bidriware", "bidriware"],
      ["Bamboo crafts", "bamboo-crafts"], ["Stone carving", "stone-carving"], ["Bronze casting", "bronze-casting"],
    ],
  },
  {
    id: "dance", kicker: "Eight schools · one grammar", h: "Classical dance traditions",
    intro: "India's classical dance forms trace their vocabulary to the Natya Shastra. Each carries centuries of temple, court and family lineage.",
    items: [
      ["Bharatanatyam", "bharatanatyam"], ["Kathak", "kathak"], ["Kathakali", "kathakali"],
      ["Kuchipudi", "kuchipudi"], ["Mohiniyattam", "mohiniyattam"], ["Manipuri", "manipuri"],
      ["Odissi", "odissi"], ["Sattriya", "sattriya"],
    ],
  },
  {
    id: "music", kicker: "From the Vedas to the village", h: "Musical traditions",
    intro: "Two great classical systems and an ocean of regional forms — Indian music is transmitted guru to shishya, parent to child, across centuries.",
    items: [
      ["Hindustani classical music", "hindustani"], ["Carnatic music", "carnatic"],
      ["Folk music", "folk-music-genre"], ["Devotional music", "devotional"], ["Tribal music", "tribal-music"],
    ],
  },
  {
    id: "clothing", kicker: "Textile as language", h: "Clothing & traditional dress",
    intro: "Every state has its own weave, drape and colour palette. Traditional attire reflects climate, craftsmanship, region and — often — family occasion.",
    items: [
      ["Saree", "saree"], ["Dhoti", "dhoti"], ["Kurta", "kurta"], ["Sherwani", "sherwani"],
      ["Lungi", "lungi"], ["Mundu", "mundu"], ["Mekhela Chador", "mekhela-chador"], ["Phiran", "phiran"],
      ["Lehenga", "lehenga"], ["Salwar Kameez", "salwar-kameez"], ["Pagri & turbans", "pagri-turban"],
    ],
  },
  {
    id: "cuisines", kicker: "Family recipes are heirlooms", h: "Regional cuisines",
    intro: "Food tells the story of migration, climate, agriculture and culture. India's regional cuisines are so distinct they could each be a country of their own.",
    items: [
      ["Punjabi", "punjabi"], ["Gujarati", "gujarati"], ["Rajasthani", "rajasthani"], ["Bengali", "bengali"],
      ["Kashmiri", "kashmiri"], ["Maharashtrian", "maharashtrian"], ["Goan", "goan"], ["Tamil", "tamil"],
      ["Telugu", "telugu"], ["Kannada", "kannada"], ["Kerala", "kerala"], ["Assamese", "assamese"],
      ["Manipuri", "manipuri-cuisine"], ["Naga", "naga"], ["Odia", "odia"],
    ],
  },
];

const DIASPORA = [
  "United States","Canada","United Kingdom","Australia","New Zealand","South Africa",
  "Fiji","Mauritius","Malaysia","Singapore","United Arab Emirates","Trinidad and Tobago",
  "Guyana","Suriname",
];

const GENEALOGY_SOURCES = [
  "Temple genealogy registers","Community genealogists","Land and revenue records",
  "Family manuscripts","Clan histories","Village archives","Court documents",
  "Colonial censuses","Family photographs","Oral traditions",
];

export default function IftIndianHeritage() {
  useEffect(() => { document.title = "Indian Culture and Heritage | IndianFamilyTree.com"; }, []);
  return (
    <div style={{ background: P.bg, color: P.ink }} data-testid="ift-indian-heritage">
      <SeoHead
        section="genealogy"
        title="Indian Culture and Heritage — Across Generations"
        description="A living essay on India's languages, religions, festivals, dance, music, clothing, cuisines and family values — one of the world's oldest continuous civilisations."
      />
      {/* Hero */}
      <section className="border-b" style={{ borderColor: P.saffronSoft, background: `linear-gradient(180deg, ${P.saffronSoft} 0%, ${P.bg} 100%)` }}>
        <div className="max-w-5xl mx-auto px-4 py-14">
          <div className="text-[10px] font-mono uppercase tracking-wider" style={{ color: P.saffron }}>Indian Heritage · A living legacy</div>
          <h1 className="font-display font-bold leading-tight text-4xl sm:text-5xl lg:text-6xl mt-2 mb-4">Indian culture and heritage — across generations.</h1>
          <p className="text-base sm:text-lg" style={{ color: P.inkSoft }}>
            Preserving the stories, traditions and values that define India. A SocialPulse heritage essay — with a page of its own for every festival, dance, cuisine, craft, faith, language and family value below. Tap any item to read a fuller note, written for the archive.
          </p>
        </div>
      </section>

      {/* Table of contents */}
      <section className="max-w-5xl mx-auto px-4 py-10">
        <div className="text-[10px] font-mono uppercase tracking-wider mb-2" style={{ color: P.saffron }}>On this page</div>
        <ol className="grid sm:grid-cols-2 gap-2 text-sm">
          {SECTIONS.map((s, i) => (
            <li key={s.id}>
              <a href={`#${s.id}`} className="inline-flex items-baseline gap-2 hover:underline" data-testid={`ift-heritage-toc-${s.id}`}>
                <span className="text-[10px] font-mono opacity-60 w-6">{String(i + 1).padStart(2, "0")}</span>
                {s.h}
              </a>
            </li>
          ))}
        </ol>
      </section>

      {/* Opening essay */}
      <section className="max-w-3xl mx-auto px-4 pb-8 space-y-4 text-base leading-relaxed" style={{ color: P.inkSoft }}>
        <p>India is one of the world's oldest continuous civilizations, with a cultural heritage spanning more than 5,000 years. From the ancient cities of the Indus Valley to today's vibrant metropolitan centres, India's identity has been shaped by countless generations of families who have preserved traditions, languages, arts, beliefs and values.</p>
        <p>Indian culture is not confined to monuments or museums — it lives in family homes, village festivals, handwritten recipes, oral histories, sacred rituals and the everyday customs passed from grandparents to grandchildren. Every family, whether rooted in a remote Himalayan village, a coastal fishing town or a bustling city, contributes a unique chapter to the story of India.</p>
        <p>At IndianFamilyTree.com, we believe that understanding your family history is also a journey into India's rich cultural and historical landscape. By preserving family stories alongside the places, communities and traditions from which they emerged, we help connect generations with the heritage that shaped their lives.</p>
        <div className="mt-6 rounded-2xl border p-5 bg-white" style={{ borderColor: P.saffronSoft }}>
          <div className="text-[10px] font-mono uppercase tracking-wider" style={{ color: P.saffron }}>A civilisation</div>
          <h3 className="font-display text-xl mt-1 mb-2" style={{ color: P.ink }}>Not just a nation.</h3>
          <p className="text-sm">India is often described as a civilisation rather than simply a modern nation-state. Its cultural foundations stretch back to the Indus Valley Civilization (c. 3300–1300 BCE), one of the earliest urban societies in the world. Archaeological sites such as Harappa and Mohenjo-daro reveal sophisticated city planning, trade networks, craftsmanship and social organisation.</p>
          <p className="text-sm mt-3">Over thousands of years, India witnessed the rise and interaction of numerous kingdoms and empires — the Mauryas, Guptas, Cholas, Pallavas, Chalukyas, Mughals, Marathas, Ahoms, Vijayanagara, the Sikh empire and many regional dynasties. Rather than replacing one another, many traditions blended and evolved, creating a society where ancient customs continue alongside modern innovation.</p>
        </div>
      </section>

      {/* Sections */}
      {SECTIONS.map((s) => (
        <section id={s.id} key={s.id} className="max-w-5xl mx-auto px-4 py-10 scroll-mt-24" data-testid={`ift-heritage-section-${s.id}`}>
          <div className="text-[10px] font-mono uppercase tracking-wider mb-1" style={{ color: P.saffron }}>{s.kicker}</div>
          <h2 className="font-display text-2xl md:text-3xl mb-3" style={{ color: P.ink }}>{s.h}</h2>
          <p className="text-sm md:text-base mb-5 max-w-3xl" style={{ color: P.inkSoft }}>{s.intro}</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {s.items.map(([label, slug], idx) => (
              <Link key={slug} to={`/indian-heritage/${slug}`} className="rounded-xl border p-3 bg-white hover:shadow-sm transition group" style={{ borderColor: P.saffronSoft }} data-testid={`ift-heritage-item-${slug}`}>
                <div className="text-[10px] font-mono opacity-60">{String(idx + 1).padStart(2, "0")}</div>
                <div className="font-display text-sm mt-0.5 flex items-center justify-between gap-2">
                  {label}
                  <ArrowRight className="w-3 h-3 opacity-40 group-hover:opacity-100 transition-opacity" style={{ color: P.saffron }} />
                </div>
              </Link>
            ))}
          </div>
        </section>
      ))}

      {/* Diaspora */}
      <section className="max-w-5xl mx-auto px-4 py-8">
        <div className="text-[10px] font-mono uppercase tracking-wider mb-1" style={{ color: P.saffron }}>The diaspora</div>
        <h2 className="font-display text-2xl md:text-3xl mb-3">Thirty million, home carried in a suitcase.</h2>
        <p className="text-sm max-w-3xl mb-5" style={{ color: P.inkSoft }}>
          More than 30 million people of Indian origin live outside India today, forming one of the world's largest diaspora communities — from Toronto to Trinidad, Sydney to Suva. Despite geographic distance, many continue to preserve languages, festivals, cuisine and family traditions while contributing to their adopted countries.
        </p>
        <div className="flex flex-wrap gap-2">
          {DIASPORA.map((c) => (
            <span key={c} className="text-xs px-3 py-1.5 rounded-full border" style={{ borderColor: P.saffronSoft, background: "#fff" }}>{c}</span>
          ))}
        </div>
      </section>

      {/* Genealogy in India */}
      <section className="max-w-5xl mx-auto px-4 py-8">
        <div className="rounded-2xl border p-6 bg-white" style={{ borderColor: P.saffronSoft }}>
          <div className="text-[10px] font-mono uppercase tracking-wider mb-1" style={{ color: P.saffron }}>Genealogy in India</div>
          <h3 className="font-display text-xl md:text-2xl mb-2">Kept in temples, panjis, land records and elders' memories.</h3>
          <p className="text-sm mb-4" style={{ color: P.inkSoft }}>
            India has practised genealogy for centuries — through temple registers at Haridwar, Puri and Varanasi; through community genealogists (pandas and bhats); through land and revenue records, family manuscripts, clan histories, village archives and colonial censuses. Technology now lets us preserve them digitally, and share them securely with relatives around the world.
          </p>
          <ul className="grid sm:grid-cols-2 gap-1 text-sm" style={{ color: P.inkSoft }}>
            {GENEALOGY_SOURCES.map((g) => <li key={g}>· {g}</li>)}
          </ul>
        </div>
      </section>

      {/* Closing */}
      <section className="max-w-5xl mx-auto px-4 pt-6 pb-16">
        <div className="rounded-3xl border p-8 md:p-10 text-center" style={{ borderColor: P.saffronSoft, background: `linear-gradient(135deg, ${P.saffronSoft} 0%, ${P.bg} 55%, ${P.goldSoft} 100%)` }}>
          <Sparkles className="w-6 h-6 mx-auto mb-3" style={{ color: P.saffron }} />
          <div className="text-[10px] font-mono uppercase tracking-wider mb-1" style={{ color: P.saffron }}>A legacy for future generations</div>
          <h3 className="font-display text-2xl md:text-3xl mb-3">Why preserving family heritage matters.</h3>
          <p className="text-sm md:text-base max-w-3xl mx-auto mb-5" style={{ color: P.inkSoft }}>
            The story of India is ultimately the story of its families. Every surname, ancestral home, recipe, song, festival and photograph adds another thread to the nation's cultural fabric. By preserving these memories today, we ensure that future generations inherit not only names and dates but also the traditions, values and experiences that give those names meaning. Whether your roots lie in a Himalayan village, a coastal town in Kerala, the deserts of Rajasthan, the tea gardens of Assam, or an Indian community overseas, your family's journey is part of India's enduring heritage.
          </p>
          <Link to="/family-trees/new" className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white shadow-sm" style={{ background: P.saffron }} data-testid="ift-heritage-cta">
            Start preserving your family legacy <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
